/**
 * Handlers for part-related operations
 */

/**
 * Handle part changes (qty, unit, etc.)
 */
export const createHandlePartChange = ({
  text,
  selectedTextId,
  setTexts,
  setSelectedText,
  isElementBeingEdited,
  setPendingChanges,
}) => {
  return (field, value, overrideUnit = null, newUnitId) => {
    if (!text.selectedContent) return;

    if (isElementBeingEdited) {
      setPendingChanges(true);
    }

    const selectedContent = text.selectedContent;
    const partInstanceId = selectedContent?.id;
    if (!partInstanceId) return;

    const updatedQty = field === "qty" ? value : selectedContent.qty || "";
    const updatedUnit =
      overrideUnit !== null ? overrideUnit : selectedContent.unit || "";
    const updatedUnitId =
      field === "unit" ? value : selectedContent.units || newUnitId || "";

    const updatedPartsByType = {};
    Object.entries(text.partsByType || {}).forEach(([typeName, typeData]) => {
      if (!typeData.parts) return;

      const updatedParts = typeData.parts.map((partObj) => {
        // Skip null or undefined partObj entries
        if (!partObj) return null;
        
        if (partObj.id === partInstanceId) {
          return {
            ...partObj,
            qty: updatedQty,
            unit: updatedUnit,
            units: updatedUnitId,
          };
        }
        return partObj;
      }).filter(Boolean);

      updatedPartsByType[typeName] = {
        ...typeData,
        parts: updatedParts,
      };
    });

    const currentPart = (() => {
      for (const typeData of Object.values(updatedPartsByType)) {
        const match = typeData.parts.find((p) => p.id === partInstanceId);
        if (match) return match.part;
      }
      return selectedContent.content?.split(" - Qty-")[0] || "";
    })();

    const updatedContentString = `${currentPart}${
      updatedQty ? ` - Qty-${updatedQty} ${updatedUnit}` : ` ${updatedUnit}`
    }`;

    // Use the provided setTexts function which should be the correct setter (setTexts or setOffCanvasTexts)
    setTexts((prevTexts) =>
      prevTexts.map((t) =>
        t.id !== selectedTextId
          ? t
          : {
              ...t,
              selectedContent: {
                ...t.selectedContent,
                qty: updatedQty,
                [field]: value,
                content: updatedContentString,
                unit: updatedUnit,
                units: updatedUnitId,
              },
              partsByType: {
                ...t.partsByType,
                ...updatedPartsByType,
              },
            }
      )
    );

    setSelectedText((prev) => ({
      ...prev,
      selectedContent: {
        ...prev.selectedContent,
        qty: updatedQty,
        [field]: value,
        content: updatedContentString,
        unit: updatedUnit,
        units: updatedUnitId,
      },
      partsByType: {
        ...prev.partsByType,
        ...updatedPartsByType,
      },
    }));
  };
};

/**
 * Handle part highlight toggle
 */
export const createHandleHighlightChange = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
  isElementBeingEdited,
  setPendingChanges,
}) => {
  return () => {
    if (!text || !text.selectedContent || text.selectedContent.type !== "part")
      return;

    if (isElementBeingEdited) {
      setPendingChanges(true);
    }

    const partInstanceId = text.selectedContent.id;
    if (!partInstanceId) return;

    setTexts((prevTexts) =>
      prevTexts.map((t) => {
        if (t.id !== text.id) return t;

        const newPartsByType = Object.fromEntries(
          Object.entries(t.partsByType || {}).map(([typeName, typeData]) => {
            const updatedParts = typeData.parts.map((partObj) =>
              partObj.id === partInstanceId
                ? { ...partObj, highlighted: !partObj.highlighted }
                : partObj
            );
            return [typeName, { ...typeData, parts: updatedParts }];
          })
        );

        return {
          ...t,
          partsByType: newPartsByType,
        };
      })
    );

    setSelectedText((prev) => {
      if (!prev) return prev;

      const updatedPartsByType = Object.fromEntries(
        Object.entries(prev.partsByType || {}).map(([typeName, typeData]) => {
          const updatedParts = typeData.parts.map((partObj) =>
            partObj.id === partInstanceId
              ? { ...partObj, highlighted: !partObj.highlighted }
              : partObj
          );
          return [typeName, { ...typeData, parts: updatedParts }];
        })
      );

      return {
        ...prev,
        partsByType: updatedPartsByType,
      };
    });

    saveToHistory();
  };
};

/**
 * Handle delete note not for part
 */
export const createHandleDeleteNotForPart = ({
  text,
  setTexts,
  setSelectedText,
  saveToHistory,
  isElementBeingEdited,
  setPendingChanges,
}) => {
  return () => {
    if (!text || !text.selectedContent || text.selectedContent.type !== "part")
      return;

    if (isElementBeingEdited) {
      setPendingChanges(true);
    }

    const selectedId = text.selectedContent.id;

    setTexts((prevTexts) =>
      prevTexts.map((t) => {
        if (t.id !== text.id) return t;

        const newPartsByType = Object.fromEntries(
          Object.entries(t.partsByType || {}).map(([typeName, typeData]) => {
            const updatedParts = typeData.parts.map((partObj) =>
              partObj.id === selectedId
                ? {
                    ...partObj,
                    note: null,
                    selectedContent: null,
                  }
                : partObj
            );
            return [typeName, { ...typeData, parts: updatedParts }];
          })
        );

        return {
          ...t,
          partsByType: newPartsByType,
        };
      })
    );

    setSelectedText((prev) => {
      if (!prev) return prev;

      const updatedPartsByType = Object.fromEntries(
        Object.entries(prev.partsByType || {}).map(([typeName, typeData]) => {
          const updatedParts = typeData.parts.map((partObj) =>
            partObj.id === selectedId
              ? { ...partObj, note: null, selectedContent: null }
              : partObj
          );
          return [typeName, { ...typeData, parts: updatedParts }];
        })
      );

      return {
        ...prev,
        partsByType: updatedPartsByType,
      };
    });

    saveToHistory();
  };
};
